import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { apiFetch } from "../lib/api";

interface Task {
  id: string;
  familyId: string;
  childId: string | null;
  taskType: string;
  dueAt: string;
  status: "pending" | "done" | "skipped" | "escalated";
  notes: string | null;
}

const TABS: Array<{ key: "today" | "overdue" | "upcoming" | "all"; label: string }> = [
  { key: "today", label: "Today" },
  { key: "overdue", label: "Overdue" },
  { key: "upcoming", label: "Upcoming" },
  { key: "all", label: "All" },
];

function Followups() {
  const [tab, setTab] = useState<"today" | "overdue" | "upcoming" | "all">("today");
  const [tasks, setTasks] = useState<Task[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = (which: typeof tab) => {
    setLoading(true);
    apiFetch<{ tasks: Task[] }>(`/api/followups?due=${which}`)
      .then((r) => setTasks(r.tasks))
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(tab);
  }, [tab]);

  const markDone = async (id: string) => {
    await apiFetch(`/api/followups/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: "done" }),
    });
    load(tab);
  };

  const skip = async (id: string) => {
    await apiFetch(`/api/followups/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ status: "skipped" }),
    });
    load(tab);
  };

  return (
    <div className="space-y-3">
      <div className="panel">
        <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">
          Screen 6
        </div>
        <h1 className="text-2xl font-semibold mb-3">Follow-Up Scheduler</h1>
        <div className="flex gap-1 border-b border-slate-200 -mb-px">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-3 py-2 text-sm border-b-2 ${
                tab === t.key
                  ? "border-slate-900 text-slate-900 font-medium"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="panel">
        {err && <div className="text-red-600 text-sm">{err}</div>}
        {loading && <div className="text-slate-500 text-sm">Loading…</div>}
        {!loading && tasks.length === 0 ? (
          <div className="text-sm text-slate-400">No tasks in this window.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 text-left">
              <tr>
                <th className="py-1">Type</th>
                <th>Due</th>
                <th>Family</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.id} className="border-t border-slate-100">
                  <td className="py-1 font-mono text-xs">{t.taskType}</td>
                  <td>{new Date(t.dueAt).toLocaleString()}</td>
                  <td>
                    <Link
                      to="/family/$familyId"
                      params={{ familyId: t.familyId }}
                      className="text-slate-700 hover:underline"
                    >
                      {t.familyId.slice(-8)}
                    </Link>
                  </td>
                  <td>
                    <span
                      className={`text-xs rounded px-1.5 ${
                        t.status === "pending"
                          ? "bg-amber-100 text-amber-800"
                          : t.status === "done"
                            ? "bg-green-100 text-green-800"
                            : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {t.status}
                    </span>
                  </td>
                  <td className="text-right">
                    {t.status === "pending" && (
                      <span className="flex justify-end gap-1">
                        <button
                          onClick={() => markDone(t.id)}
                          className="text-xs rounded bg-slate-900 text-white px-2 py-1"
                        >
                          done
                        </button>
                        <button
                          onClick={() => skip(t.id)}
                          className="text-xs rounded bg-slate-100 text-slate-700 px-2 py-1"
                        >
                          skip
                        </button>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export const Route = createFileRoute("/followups")({ component: Followups });
